<template>
  <div class="group-events-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ t('menu.group_events') }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 时间线布局 -->
        <div v-else-if="sortedEvents && sortedEvents.length > 0" class="timeline-container">
          <div class="timeline">
            <!-- 每个年份的事件组 -->
            <div
              v-for="(yearItem, yearIndex) in sortedEvents"
              :key="yearItem.year"
              class="timeline-year-group"
              :class="{ 'animate__animated': true, 'animate__fadeInUp': true }"
              :style="`animation-delay: ${yearIndex * 0.1}s;`"
            >
              <!-- 年份标记 -->
              <div class="year-marker">
                <div class="year-badge">
                  <span class="year-number">{{ yearItem.year }}</span>
                  <div class="year-dot"></div>
                </div>
                <div class="year-line"></div>
              </div>

              <!-- 事件列表 -->
              <div class="events-list">
                <div
                  v-for="(event, eventIndex) in yearItem.events"
                  :key="`${yearItem.year}-${eventIndex}`"
                  class="event-item"
                  :class="{
                    'animate__animated': true,
                    'animate__fadeInRight': true
                  }"
                  :style="`animation-delay: ${yearIndex * 0.1 + eventIndex * 0.05}s;`"
                >
                  <!-- 月份标记 -->
                  <div v-if="event.month" class="month-tag">
                    <span class="month-text">{{ t(`month.${event.month}`) }}</span>
                  </div>
                  <div v-else class="month-tag uncategorized">
                    <span class="month-text">-</span>
                  </div>

                  <!-- 事件内容 -->
                  <div class="event-content">
                    <p class="event-text">{{ event.event }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 时间线结束标记 -->
<!--          <div class="timeline-end">-->
<!--            <div class="end-circle"></div>-->
<!--            <div class="end-text">{{ t('common.present') }}</div>-->
<!--          </div>-->
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="empty-state"
        >
          <div class="empty-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 70C56.5685 70 70 56.5685 70 40C70 23.4315 56.5685 10 40 10C23.4315 10 10 23.4315 10 40C10 56.5685 23.4315 70 40 70Z"
                stroke="var(--el-color-primary-light-8)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M40 30V40"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M40 50H40.025"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">{{ t('common.no_data') }}</h3>
          <p class="empty-description">{{ t('common.no_events') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning } from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const eventsData = ref([])
const isLoading = ref(false)
const errorMessage = ref('')

// 计算属性：按年份排序的事件列表
const sortedEvents = computed(() => {
  if (!eventsData.value || eventsData.value.length === 0) {
    return []
  }

  // 按年份降序排序（最新的在前）
  return [...eventsData.value]
    .sort((a, b) => b.year - a.year)
    .map(item => {
      // 每个年份内的事件按月份排序（有月份的在前面按月份升序，未分类的在后面）
      const sortedEvents = [...item.events].sort((a, b) => {
        if (a.month === null && b.month !== null) return 1
        if (b.month === null && a.month !== null) return -1
        return (a.month || 0) - (b.month || 0)
      })
      return { ...item, events: sortedEvents }
    })
})

// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/history' // 假设这是集团大事记的固定路径
    })

    if (response && response.content) {
      eventsData.value = response.content.list || []
    } else {
      eventsData.value = []
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})
</script>

<style scoped lang="scss">
// 导入全局样式变量
@import '@/assets/css/common';
@import '@/assets/css/business';

// 时间线容器
.timeline-container {
  position: relative;
  max-width: 900px;
  margin: 0 auto;
}

// 时间线
.timeline {
  position: relative;
  padding-left: 100px;

  @media (max-width: 768px) {
    padding-left: 80px;
  }

  @media (max-width: 480px) {
    padding-left: 60px;
  }

  // 时间线中心线
  &::before {
    content: '';
    position: absolute;
    left: 95px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, $primary-light-8, $primary-light-6, $primary-light-8);

    @media (max-width: 768px) {
      left: 75px;
    }

    @media (max-width: 480px) {
      left: 55px;
    }
  }
}

// 年份组
.timeline-year-group {
  position: relative;
  margin-bottom: 60px;

  &:last-child {
    margin-bottom: 40px;
  }

  @media (max-width: 768px) {
    margin-bottom: 50px;
  }

  @media (max-width: 480px) {
    margin-bottom: 40px;
  }
}

// 年份标记
.year-marker {
  position: absolute;
  left: -100px;
  top: 0;
  width: 100px;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    left: -80px;
    width: 80px;
  }

  @media (max-width: 480px) {
    left: -60px;
    width: 60px;
  }
}

.year-badge {
  position: relative;
  z-index: 2;
}

.year-number {
  display: block;
  font-family: $font-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: $primary-color;
  text-align: right;
  padding-right: 15px;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    padding-right: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
    padding-right: 10px;
  }
}

.year-dot {
  position: absolute;
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  background: $primary-color;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 0 0 3px rgba($primary-color, 0.2);

  @media (max-width: 768px) {
    width: 12px;
    height: 12px;
    border-width: 2px;
    box-shadow: 0 0 0 2px rgba($primary-color, 0.2);
  }

  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
  }
}

.year-line {
  flex: 1;
  height: 2px;
  background: $primary-color;
  opacity: 0.3;
}

// 事件列表
.events-list {
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
}

// 单个事件项
.event-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    margin-bottom: 20px;
  }
}

.month-tag {
  flex-shrink: 0;
  background: linear-gradient(135deg, $primary-light-9, $primary-light-7);
  color: $primary-dark-2;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-right: 20px;
  border: 1px solid rgba($primary-color, 0.1);
  min-width: 80px;
  text-align: center;

  &.uncategorized {
    background: linear-gradient(135deg, #f5f5f5, #e0e0e0);
    color: $text-light;
  }

  @media (max-width: 768px) {
    padding: 6px 14px;
    font-size: 0.8125rem;
    margin-right: 16px;
    min-width: 70px;
  }

  @media (max-width: 480px) {
    margin-right: 0;
    margin-bottom: 12px;
    align-self: flex-start;
  }
}

.month-text {
  display: block;
}

.event-content {
  flex: 1;
}

.event-text {
  font-size: 1.125rem;
  line-height: 1.6;
  color: $text-color;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}

// 时间线结束标记
.timeline-end {
  position: relative;
  display: flex;
  align-items: center;
  padding-left: 100px;
  margin-top: 40px;

  @media (max-width: 768px) {
    padding-left: 80px;
  }

  @media (max-width: 480px) {
    padding-left: 60px;
    margin-top: 30px;
  }
}

.end-circle {
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, $secondary-color, $primary-color);
  border-radius: 50%;
  position: absolute;
  left: 95px;
  transform: translateX(-50%);
  box-shadow: 0 0 0 4px rgba($secondary-color, 0.2);

  @media (max-width: 768px) {
    left: 75px;
    width: 18px;
    height: 18px;
  }

  @media (max-width: 480px) {
    left: 55px;
    width: 16px;
    height: 16px;
  }
}

.end-text {
  font-size: 1rem;
  color: $primary-color;
  font-weight: 600;
  padding-left: 30px;

  @media (max-width: 768px) {
    padding-left: 25px;
  }

  @media (max-width: 480px) {
    padding-left: 20px;
    font-size: 0.9375rem;
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.error-icon {
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-title {
  font-size: 1.5rem;
  color: #f56c6c;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.retry-button {
  background: $primary-color;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-light-3;
    transform: translateY(-2px);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}
</style>